import { z } from "zod";
import { WhyPointSchema } from "./level-assessment";

/**
 * @public — the FUSION contract: evidence-triggered per-axis re-scoring for
 * rounds ≥ 2 (`docs/labs/level-assessment/multi-round-update-model.md`).
 *
 * Round 1 scores from a structured LinkedIn signal vector; every later round
 * adds mostly-unstructured text evidence. This module is the traceability
 * spine that makes that evidence move a level defensibly:
 *
 *   - **{@link EvidenceClaim}** — an atomic, quote-verified observation
 *     extracted from one packet (P1).
 *   - **{@link OpenPoint}** — a persisted, per-round ledger entry for
 *     everything that, if evidenced, would change the assessment.
 *   - **{@link RoundUpdateRecord}** — the full traceability artifact of one
 *     round: the claims extracted, what happened to each packet, the ledger
 *     state after the round, and (when the round re-scored) the movement.
 *
 * Deliberately imports {@link WhyPointSchema} from `./level-assessment` (never
 * the other way) — `level-assessment.ts` must stay import-cycle-free of this
 * file; the compact, contract-version-agnostic `MovementSummary` embedded in
 * `LevelAssessmentResult` lives there instead, precisely to avoid this file
 * needing to be imported by the base result contract.
 */

/** Payload schema version. Bump when the `RoundUpdateRecord` shape changes in
 *  a way that should be visible to readers of the persisted row (the side
 *  table stores it verbatim; this is NOT a cache key like the panel's — there
 *  is no regeneration, only append). v2 = the frontier redesign
 *  (`multi-round-frontier-redesign.md`): claim credibility features +
 *  discrepancies, the `demonstration` claim kind, ledger probe priority, and
 *  the round saturation signal — all additive, v1 rows still parse. */
export const ROUND_UPDATE_VERSION = 2;

// ── Evidence claims (P1) ────────────────────────────────────────────────────

/** The packet type a claim was extracted from — mirrors the operator-chosen
 *  `InformationPacketType`, minus `linkedin` (structured, never a claim
 *  source). This is the ONLY thing that determines a claim's trust tier —
 *  never `speaker` (display metadata only, spoofable text content). */
export const ClaimSourceTypeSchema = z.enum(["cv", "interview_transcript", "free_text"]);
export type ClaimSourceType = z.infer<typeof ClaimSourceTypeSchema>;

/** The extractor's core judgment: how much this excerpt actually establishes.
 *  `strong` = specific first-person or interviewer-probed account with
 *  mechanics/scope/outcomes; `moderate` = clear, credible statement with some
 *  specificity; `weak` = off-hand mention, generic self-description, list-item
 *  without depth. Weak evidence never moves a level by itself. */
export const ClaimStrengthSchema = z.enum(["strong", "moderate", "weak"]);
export type ClaimStrength = z.infer<typeof ClaimStrengthSchema>;

/** `testimony` = an account of past behavior/facts. `demonstration` =
 *  behavior or an artifact directly EXHIBITED inside the packet itself (a
 *  live demo, real-time problem-solving, the candidate's own probing
 *  questions) — observed behavior, the highest-validity evidence class
 *  (work-sample logic), and it satisfies the same gates testimony does with a
 *  trust premium in integration. `thin_response` = a substantive competency
 *  question was visibly raised in the transcript and met a generic/evasive/
 *  shallow answer — informative absence, never a level mover, but dents
 *  confidence on repeated engagement without depth. Pure absence (the topic
 *  never surfaced) produces no claim at all. */
export const ClaimKindSchema = z.enum(["testimony", "demonstration", "thin_response"]);
export type ClaimKind = z.infer<typeof ClaimKindSchema>;

/** C2 (frontier redesign) — the credibility sub-features `strength` is
 *  DERIVED from, mechanically (checklist-not-single-tell: the verifiability
 *  approach + CBCA/RM content criteria). Kept on the claim so the derivation
 *  is auditable. Credibility is about whether the claim is likely TRUE —
 *  never about how senior the described work is (that is the judge's job). */
export const ClaimCredibilitySchema = z.object({
  /** Checkable specifics: a named artifact, dated timeframe, named
   *  counterparty/system, or reproducible metric that a follow-up could
   *  confirm. Graded, not boolean (partial verifiability is common). */
  verifiability: z.enum(["checkable", "partial", "unverifiable"]),
  /** Mechanics/scope/outcome depth of the account. */
  specificity: z.enum(["high", "medium", "low"]),
  /** First-person causal role: "eu liderei/decidi" (direct) vs "nós/o time"
   *  (shared) vs bystander framing (peripheral). */
  agency: z.enum(["direct", "shared", "peripheral"]),
  /** Concrete numbers present in the quote. */
  quantified: z.boolean(),
  /** Spontaneous self-correction / unprompted complication preserved in the
   *  quote — a reality-monitoring cue; requires uncleaned transcripts. */
  selfCorrection: z.boolean(),
});
export type ClaimCredibility = z.infer<typeof ClaimCredibilitySchema>;

/** C4 (frontier redesign) — the excerpt conflicts with another source: the
 *  structured profile facts (dates, roles, sizes) or an earlier round's
 *  claim. Surfaced neutrally ("a verificar") — the system reports conflicts,
 *  never infers intent. Ledger evolution turns unresolved discrepancies into
 *  `divergence` open points. */
export const ClaimDiscrepancySchema = z.object({
  against: z.enum(["profile", "prior_claim"]),
  /** pt-BR, names both sides factually. */
  note: z.string(),
});
export type ClaimDiscrepancy = z.infer<typeof ClaimDiscrepancySchema>;

/** Display metadata ONLY — never used to weight a claim (spoofable text
 *  content; trust keys exclusively on the operator-chosen packet type). */
export const ClaimSpeakerSchema = z.enum(["candidate", "interviewer", "document", "unclear"]);

/** One axis a claim was routed to. */
export const ClaimAxisRefSchema = z.object({
  scope: z.enum(["cross", "vertical"]),
  axisId: z.string(),
});

/** An atomic, quote-anchored observation extracted from one text packet. A
 *  claim whose quote is not found verbatim (whitespace-normalized) in the
 *  packet is dropped before it ever reaches this shape — hallucinated
 *  evidence cannot enter the system. Level-blind: the extractor never sees
 *  the prior score and never proposes levels. */
export const EvidenceClaimSchema = z.object({
  /** Stable id, scheme `"cl-<round seq>-<n>"`. */
  id: z.string(),
  packetId: z.string(),
  sourceType: ClaimSourceTypeSchema,
  /** Display metadata only — see {@link ClaimSpeakerSchema}. */
  speaker: ClaimSpeakerSchema,
  kind: ClaimKindSchema,
  /** Routed axes, 0..3. Empty = unroutable — becomes ledger material
   *  ("a verificar"), never scoring input. */
  axes: z.array(ClaimAxisRefSchema).max(3).default([]),
  strength: ClaimStrengthSchema,
  /** Verbatim excerpt, ≤ ~300 chars, VERIFIED against the packet. */
  quote: z.string(),
  /** pt-BR: what this excerpt establishes (observation, not judgment). */
  finding: z.string(),
  /** Short label for grouping/UI. */
  topic: z.string(),
  /** Open points (incl. global ones) this claim bears on. */
  addressesPointIds: z.array(z.string()).default([]),
  /** v2 — the sub-features `strength` was derived from (absent on v1 rows,
   *  whose strength was a holistic label). */
  credibility: ClaimCredibilitySchema.optional(),
  /** v2 — set when the excerpt conflicts with the profile or a prior claim. */
  discrepancy: ClaimDiscrepancySchema.optional(),
});
export type EvidenceClaim = z.infer<typeof EvidenceClaimSchema>;

// ── The open-points ledger ───────────────────────────────────────────────────

/** What kind of gap an open point represents. */
export const OpenPointTypeSchema = z.enum([
  /** Abstained with nothing observed at all. */
  "no_signal",
  /** Abstained despite some observed evidence — inconclusive, not invisible. */
  "abstention",
  /** Scored but with confidence below the low-confidence threshold. */
  "low_confidence",
  /** A vertical axis whose level fell outside the discipline's sub-band. */
  "out_of_band",
  /** The scored axes disagree widely — a spread worth resolving. */
  "irregular",
  /** The model verdict is inconclusive — worth a targeted probe. */
  "divergence",
  /** A previous round's suggested probe, still to resolve. */
  "follow_up",
  /** Anti-anchoring: a confidently-scored axis resting only on the LinkedIn
   *  baseline, never yet corroborated by any other source. */
  "single_source",
]);
export type OpenPointType = z.infer<typeof OpenPointTypeSchema>;

/** The point's resolution state. */
export const OpenPointStatusSchema = z.enum([
  "open",
  "partially_addressed",
  "addressed",
  "refuted",
]);
export type OpenPointStatus = z.infer<typeof OpenPointStatusSchema>;

/** Qualifies HOW a point was resolved — "addressed" alone would collapse
 *  *learned they're strong* with *learned they're weak*. Client copy renders
 *  the direction ("endereçado — confirmou o nível" vs "endereçado — revisou
 *  para baixo"). */
export const OpenPointOutcomeSchema = z.enum([
  "confirmed",
  "strengthened",
  "weakened",
  "inconclusive",
]);
export type OpenPointOutcome = z.infer<typeof OpenPointOutcomeSchema>;

/** One round's verdict on a point, appended to its `history`. */
export const OpenPointHistoryEntrySchema = z.object({
  roundId: z.string(),
  verdict: OpenPointStatusSchema,
  outcome: OpenPointOutcomeSchema.nullable(),
  note: z.string(),
  claimIds: z.array(z.string()),
});
export type OpenPointHistoryEntry = z.infer<typeof OpenPointHistoryEntrySchema>;

/** A persisted, per-round ledger entry — everything that, if evidenced,
 *  would change the assessment. `axisId: null` = global (profile-wide
 *  irregularity, not tied to one axis). Ownership of status transitions is
 *  split: most types resolve deterministically from assessment state; only
 *  `divergence`/`follow_up` carry a genuine model verdict (see the design
 *  doc's resolution-authority table). */
export const OpenPointSchema = z.object({
  /** Stable id, scheme `"op-<round seq>-<n>"`, stable across rounds. */
  id: z.string(),
  scope: z.enum(["cross", "vertical"]).nullable(),
  axisId: z.string().nullable(),
  type: OpenPointTypeSchema,
  /** pt-BR. */
  title: z.string(),
  /** pt-BR: what is unknown and what evidence would settle it. */
  description: z.string(),
  /** Suggested interview probe, pt-BR. Null when not applicable. */
  probe: z.string().nullable(),
  status: OpenPointStatusSchema,
  outcome: OpenPointOutcomeSchema.nullable(),
  /** How many rounds engaged this point with only thin substance — the memory
   *  behind the "engaged-but-thin dents confidence" doctrine rule. */
  probedThinRounds: z.number().int().nonnegative().default(0),
  raisedAtRoundId: z.string(),
  history: z.array(OpenPointHistoryEntrySchema).default([]),
  /** v2 — deterministic probe priority (CAT-style expected information:
   *  normalized axis weight × uncertainty × probe-utility). Higher = probe
   *  first; 0 = the axis is settled and further probing has near-zero
   *  expected value. Absent on v1 rows (renderers fall back to raised
   *  order). */
  priority: z.number().min(0).optional(),
});
export type OpenPoint = z.infer<typeof OpenPointSchema>;

// ── Per-packet outcome ───────────────────────────────────────────────────────

/** What happened to one packet during extraction — persisted so a "why
 *  didn't this move anything" question is always answerable from the row. */
export const PacketOutcomeSchema = z.object({
  packetId: z.string(),
  status: z.enum(["extracted", "duplicate", "failed"]),
  note: z.string().default(""),
});
export type PacketOutcome = z.infer<typeof PacketOutcomeSchema>;

// ── Per-axis update (P3) ─────────────────────────────────────────────────────

/** The per-axis movement verdict. `update_failed` marks an LLM failure — it is
 *  never presented as a judged "held". `evidence_invalidated` is a justified
 *  re-abstention (non-weak contradicting evidence shows the prior level's
 *  basis was false); ordinary doubt is expressed as lower confidence, never
 *  re-abstention. */
export const AxisUpdateVerdictSchema = z.enum([
  "raised",
  "lowered",
  "held",
  "scored_from_abstention",
  "still_abstained",
  "evidence_invalidated",
  "update_failed",
]);
export type AxisUpdateVerdict = z.infer<typeof AxisUpdateVerdictSchema>;

/** How much weight one cited claim carried in an axis's movement — exists so
 *  a claim already `decisive` for a movement is not re-spent in a later
 *  round's judgment (anti "evidence re-banking"). */
export const ClaimDispositionSchema = z.object({
  claimId: z.string(),
  weight: z.enum(["decisive", "corroborative", "insufficient", "contradicted"]),
});
export type ClaimDisposition = z.infer<typeof ClaimDispositionSchema>;

/** One open point's resolution as decided during an axis's update. */
export const PointResolutionSchema = z.object({
  pointId: z.string(),
  verdict: OpenPointStatusSchema,
  outcome: OpenPointOutcomeSchema.nullable(),
  note: z.string(),
  claimIds: z.array(z.string()),
});
export type PointResolution = z.infer<typeof PointResolutionSchema>;

/** One axis's update record for this round — carried axes never get one (they
 *  are bit-identical copies of the prior assessment, by construction). */
export const AxisUpdateSchema = z.object({
  scope: z.enum(["cross", "vertical"]),
  axisId: z.string(),
  fromLevel: z.number().int().nullable(),
  toLevel: z.number().int().nullable(),
  fromConfidence: z.number().min(0).max(1),
  toConfidence: z.number().min(0).max(1),
  verdict: AxisUpdateVerdictSchema,
  /** Deterministic, computed on UNCLAMPED levels: |Δ| ≥ 2 rungs. Client copy
   *  uses a neutral frame ("salto de nível — revisar evidência"). */
  anomalous: z.boolean(),
  /** pt-BR, cites claims. */
  rationale: z.string(),
  claimIds: z.array(z.string()).default([]),
  claimDispositions: z.array(ClaimDispositionSchema).default([]),
  pointResolutions: z.array(PointResolutionSchema).default([]),
  whyThisLevel: z.array(WhyPointSchema).default([]),
  whyNotHigher: z.array(WhyPointSchema).default([]),
  whyNotLower: z.array(WhyPointSchema).default([]),
  /** Present when still/newly abstained — what evidence would settle it. */
  missingEvidence: z.array(z.string()).default([]),
});
export type AxisUpdate = z.infer<typeof AxisUpdateSchema>;

// ── Movement decomposition (P4) ──────────────────────────────────────────────

/** The four term families the mean movement decomposes into exactly (§4/P4 of
 *  the design doc). `level_change` = a touched axis moved; `renormalization`
 *  = a weight-mass shift as axes entered/left the scored set; `entering` = an
 *  axis scored from abstention; `leaving` = an axis returned to abstention
 *  (refresh-round re-baseline or `evidence_invalidated`). */
export const MovementTermKindSchema = z.enum([
  "level_change",
  "renormalization",
  "entering",
  "leaving",
]);

/** One axis's signed contribution to the mean movement (recentered — reads as
 *  "pull relative to the prior mean"). Terms sum exactly to `overallMovement`'s
 *  `toMean - fromMean` (property-tested). */
export const MovementTermSchema = z.object({
  axisId: z.string(),
  term: z.number(),
  kind: MovementTermKindSchema,
});
export type MovementTerm = z.infer<typeof MovementTermSchema>;

/** The round's overall movement — the mean is the invariant surface (an
 *  integer-level change is presented as a rounding-threshold crossing, never
 *  as a free-standing jump). */
export const OverallMovementSchema = z.object({
  fromMean: z.number().nullable(),
  toMean: z.number().nullable(),
  fromLevel: z.number().int().nullable(),
  toLevel: z.number().int().nullable(),
  decomposition: z.array(MovementTermSchema).default([]),
  /** pt-BR, composed by the movement explainer (P5) from the decomposition +
   *  per-axis rationales — never invents magnitudes. */
  narrative: z.string().default(""),
});

/** The round's full movement record — present only when the round re-scored
 *  (touched axes non-empty, or a refresh-round baseline shift). */
export const RoundMovementSchema = z.object({
  priorEvaluationId: z.string().nullable(),
  /** Set only for a refreshed-baseline round (§6) — the engine's own re-run,
   *  distinct from the fused result this movement otherwise describes. */
  baselineEvaluationId: z.string().nullable().default(null),
  baseline: z.enum(["prior", "refreshed_profile"]),
  axisUpdates: z.array(AxisUpdateSchema),
  overallMovement: OverallMovementSchema,
});
export type RoundMovement = z.infer<typeof RoundMovementSchema>;

// ── The round update record (persisted row view) ────────────────────────────

/** The full traceability artifact of one round — what the
 *  `level_assessment_round_updates` side table stores, parsed. `movement` is
 *  null for a round that produced no new evaluation (nothing routed / all
 *  packets duplicates / round 1's bootstrap). */
/** v2 — the round's assessment-saturation read: how much of the scored
 *  weight mass is SETTLED (confidence ≥ threshold, ≥ 2 independent
 *  non-LinkedIn sources, no open contradiction), and whether another
 *  interview round is still expected to move the assessment. Converts the
 *  diminishing-returns literature (~3-4 substantive rounds) into a visible
 *  stop signal instead of an open-ended probe loop. */
export const RoundSaturationSchema = z.object({
  /** Share (0..1) of the scored weight mass on settled axes. */
  settledWeightShare: z.number().min(0).max(1),
  /** Open (probe-worthy) points remaining after this round. */
  openCount: z.number().int().nonnegative(),
  recommendation: z.enum(["continue_probing", "diminishing_returns"]),
});
export type RoundSaturation = z.infer<typeof RoundSaturationSchema>;

export const RoundUpdateRecordSchema = z.object({
  version: z.number().int(),
  roundId: z.string(),
  candidateId: z.string(),
  evaluationId: z.string().nullable(),
  claims: z.array(EvidenceClaimSchema),
  packetOutcomes: z.array(PacketOutcomeSchema),
  /** The full open-point state AFTER this round (immutable snapshot). */
  ledger: z.array(OpenPointSchema),
  movement: RoundMovementSchema.nullable(),
  /** v2 — absent on v1 rows and on rounds that produced no assessment. */
  saturation: RoundSaturationSchema.optional(),
});
export type RoundUpdateRecord = z.infer<typeof RoundUpdateRecordSchema>;

// ── The get-round-update usecase contract ───────────────────────────────────

/** Fetch one round's traceability record — the ledger + claims + movement.
 *  Absent (no row yet — e.g. a legacy round, or a fusion round still
 *  computing) is a first-class, non-error response. */
export const GetRoundUpdateInputSchema = z.object({
  candidateId: z.string().uuid(),
  roundId: z.string().uuid(),
});

export type GetRoundUpdateResponse =
  | { status: "absent" }
  | { status: "ready"; record: RoundUpdateRecord };
